import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, Badge, Button, Input, Modal } from '../components/ui';
import { api, ApiError } from '../lib/api';
import type { ApiResponse } from '../lib/types';

interface Item {
  itemId?: string; name?: string; url?: string; urlclean?: string; urlwithtracking?: string; tracking_code?: string;
  sku?: string; type?: string; category?: string; price?: number; salesPrice?: number; currency?: string;
  catalogActive?: boolean; inStock?: boolean | null; clicks?: number; impressions?: number; recommendations?: number;
  product?: number; cart?: number; purchase?: number; linkId?: string;
}
interface DestData { pulse_feed?: { feed_id: string; account_id: string }; id?: string; name?: string; type?: string; status?: string; feed_url?: string; created_at?: string; tracking_code?: string; items?: Item[] }

const n = (v: unknown) => Number(v) || 0;
const SORTS = [
  { k: 'clicks', l: '📊 Clicks' }, { k: 'product', l: '👁 Product' }, { k: 'cart', l: '🛒 Cart' }, { k: 'purchase', l: '💳 Purchase' },
];

/** Extrai a querystring de uma URL como pares chave/valor legíveis. */
function parseQuery(url?: string): { key: string; value: string }[] {
  if (!url) return [];
  const qs = url.split('?')[1];
  if (!qs) return [];
  return qs.split('&').filter(Boolean).map((pair) => {
    const [k, ...rest] = pair.split('=');
    const raw = rest.join('=');
    let value = raw;
    try { value = decodeURIComponent(raw.replace(/\+/g, ' ')); } catch { /* mantém cru */ }
    return { key: decodeURIComponent(k || ''), value };
  });
}

export default function DestinationView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dest, setDest] = useState<DestData | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('clicks');
  const [dir, setDir] = useState<'desc' | 'asc'>('desc');
  const [trackItem, setTrackItem] = useState<Item | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function clearItems() {
    if (!id) return;
    if (!confirm(`Delete all ${items.length} item(s) from this destination set? The set itself is kept, so you can import again.`)) return;
    setBusy('clear');
    try {
      const r = await api.post<{ deleted?: number }>('/destinations/items/clear', { destination_set_id: id });
      alert(`${r.deleted ?? 0} item(s) deleted.`);
      load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Failed to clear items');
    } finally {
      setBusy(null);
    }
  }

  async function deleteSet() {
    if (!id) return;
    if (!confirm('Delete this destination set and all of its items? This cannot be undone.')) return;
    setBusy('delete');
    try {
      await api.del(`/destinations/delete?id=${id}`);
      navigate('/destinations');
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Failed to delete destination set');
      setBusy(null);
    }
  }

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      api.get<ApiResponse<DestData>>(`/destinations/get?id=${id}`),
      api.get<ApiResponse<Item[]> & { total?: number }>(`/destinations/items?destination_set_id=${id}&include_inactive=${includeInactive}`),
    ]).then(([d, it]) => { setDest(d.data || null); setItems(it.data || []); setTotal(it.total ?? it.data?.length ?? 0); setError(null); })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id, includeInactive]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q ? items.filter((i) => `${i.url || ''} ${i.sku || ''} ${i.itemId || ''} ${i.name || ''}`.toLowerCase().includes(q)) : items;
    const key = sortKey as keyof Item;
    return [...base].sort((a, b) => (dir === 'desc' ? n(b[key]) - n(a[key]) : n(a[key]) - n(b[key])));
  }, [items, search, sortKey, dir]);

  const clickSort = (k: string) => { if (k === sortKey) setDir((d) => (d === 'desc' ? 'asc' : 'desc')); else { setSortKey(k); setDir('desc'); } };
  const trunc = (s: string | undefined, len = 40) => (s ? (s.length > len ? s.slice(0, len) + '…' : s) : '-');

  return (
    <Layout
      title="Destination Set"
      actions={
        <>
          {!dest?.pulse_feed && <Button variant="outline" onClick={() => navigate(`/destinations/edit/${id}`)}>Edit</Button>}
          {!dest?.pulse_feed && items.length > 0 && (
            <Button className="bg-amber-500 hover:bg-amber-600" onClick={clearItems} disabled={!!busy}>
              {busy === 'clear' ? '…' : '🗑 Clear items'}
            </Button>
          )}
          {!dest?.pulse_feed && <Button variant="danger" onClick={deleteSet} disabled={!!busy}>
            {busy === 'delete' ? '…' : '× Delete'}
          </Button>}
          <Button variant="ghost" onClick={() => navigate('/destinations')}>← Back</Button>
        </>
      }
    >
      {error && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{error}</div>}
      {loading ? <Card className="p-10 text-center text-slate-400">Loading…</Card> : (
        <>
          <Card className="p-5 mb-5">
            <table className="text-sm">
              <tbody>
                <tr><th className="text-left pr-6 py-1 text-slate-500 font-medium">ID:</th><td className="font-mono text-xs">{dest?.id}</td></tr>
                <tr><th className="text-left pr-6 py-1 text-slate-500 font-medium">Name:</th><td className="font-medium text-ink-900">{dest?.name}</td></tr>
                <tr><th className="text-left pr-6 py-1 text-slate-500 font-medium">Type:</th><td><Badge tone={dest?.type === 'feed' ? 'blue' : 'brand'}>{(dest?.type || '').toUpperCase()}</Badge></td></tr>
                <tr><th className="text-left pr-6 py-1 text-slate-500 font-medium">Status:</th><td><Badge tone={dest?.status === 'active' ? 'green' : 'neutral'}>{dest?.status}</Badge></td></tr>
                {dest?.feed_url && <tr><th className="text-left pr-6 py-1 text-slate-500 font-medium">Feed URL:</th><td><a className="text-brand-600 underline text-xs break-all" href={dest.feed_url} target="_blank" rel="noreferrer">{dest.feed_url}</a></td></tr>}
                {dest?.tracking_code && <tr><th className="text-left pr-6 py-1 text-slate-500 font-medium">Tracking Code:</th><td className="font-mono text-xs text-slate-500">{dest.tracking_code}</td></tr>}
                <tr><th className="text-left pr-6 py-1 text-slate-500 font-medium">Created:</th><td className="text-slate-500">{dest?.created_at ? new Date(dest.created_at).toLocaleString() : 'N/A'}</td></tr>
              </tbody>
            </table>
          </Card>

          <h2 className="text-lg font-semibold text-ink-900 mb-3">Items ({total})</h2>
          {dest?.pulse_feed && <div className="mb-3 text-sm">Managed by PulseDSP · Feed {dest.pulse_feed.feed_id}
            <label className="ml-4"><input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} /> Include removed products and their history</label>
          </div>}
          {items.length < total && <p>Showing {items.length} of {total} products.</p>}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="w-full md:w-96"><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 Search by URL, SKU, Item ID or Name…" /></div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Sort by:</span>
              {SORTS.map((s) => (
                <button key={s.k} onClick={() => clickSort(s.k)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium ${sortKey === s.k ? 'bg-chrome-800 text-white' : 'bg-surface-muted text-slate-600 hover:bg-slate-200'}`}>
                  {s.l}{sortKey === s.k ? (dir === 'desc' ? ' ↓' : ' ↑') : ''}
                </button>
              ))}
            </div>
          </div>

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 bg-surface-muted">
                    <th className="px-2 py-2.5 font-medium">#</th>
                    <th className="px-2 py-2.5 font-medium">Item ID</th>
                    <th className="px-2 py-2.5 font-medium">Name</th>
                    <th className="px-2 py-2.5 font-medium">URL</th>
                    <th className="px-2 py-2.5 font-medium">URL with Tracking</th>
                    <th className="px-2 py-2.5 font-medium">SKU</th>
                    <th className="px-2 py-2.5 font-medium">Type</th>
                    <th className="px-2 py-2.5 font-medium">Category</th>
                    <th className="px-2 py-2.5 font-medium">Price</th>
                    <th className="px-2 py-2.5 font-medium text-right">Clicks</th>
                    <th className="px-2 py-2.5 font-medium text-right">Impressions</th>
                    <th className="px-2 py-2.5 font-medium text-right">Recommendations</th>
                    <th className="px-2 py-2.5 font-medium text-right">Product</th>
                    <th className="px-2 py-2.5 font-medium text-right">Cart</th>
                    <th className="px-2 py-2.5 font-medium text-right">Purchase</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border align-top">
                  {filtered.map((it, i) => (
                    <tr key={(it.itemId || '') + i} className="hover:bg-surface-muted/60">
                      <td className="px-2 py-2 text-slate-400">{i + 1}</td>
                      <td className="px-2 py-2 font-mono text-[10px] text-slate-500 max-w-[110px] break-all">{trunc(it.itemId, 24)}</td>
                      <td className="px-2 py-2 font-medium text-ink-900 max-w-[160px] break-words">{trunc(it.name, 60)}</td>
                      <td className="px-2 py-2 max-w-[240px] space-y-0.5">
                        <div>
                          <span className="text-slate-400">URL:</span>{' '}
                          {it.url ? <a className="text-brand-600 hover:underline break-all" href={it.url} target="_blank" rel="noreferrer" title={it.url}>{trunc(it.url, 46)}</a> : <span className="text-slate-300">-</span>}
                        </div>
                        {it.urlwithtracking && (
                          <div>
                            <span className="text-slate-400">URL with Tracking Codes:</span>{' '}
                            <a className="text-brand-600 hover:underline break-all" href={it.urlwithtracking} target="_blank" rel="noreferrer" title={it.urlwithtracking}>{trunc(it.urlwithtracking, 46)}</a>
                          </div>
                        )}
                        {it.urlclean && (
                          <div>
                            <span className="text-slate-400">Clean:</span>{' '}
                            <a className="text-brand-600 hover:underline break-all" href={it.urlclean} target="_blank" rel="noreferrer" title={it.urlclean}>{trunc(it.urlclean, 46)}</a>
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 max-w-[220px]">
                        {it.urlwithtracking || it.linkId ? (
                          <>
                            {it.urlwithtracking && (
                              <a className="text-brand-600 hover:underline break-all" href={it.urlwithtracking} target="_blank" rel="noreferrer" title={it.urlwithtracking}>{trunc(it.urlwithtracking, 50)}</a>
                            )}
                            {it.linkId && <div className="text-slate-400 mt-0.5">LinkId: {trunc(it.linkId, 12)}</div>}
                            <button type="button" onClick={() => setTrackItem(it)} className="mt-1 text-[11px] text-brand-600 hover:underline font-medium">
                              🔎 View Tracking
                            </button>
                          </>
                        ) : <span className="text-slate-300">-</span>}
                      </td>
                      <td className="px-2 py-2 text-slate-500 max-w-[130px] break-all">{it.sku || '-'}</td>
                      <td className="px-2 py-2"><Badge tone={it.type === 'product' ? 'blue' : 'neutral'}>{it.type || '-'}</Badge></td>
                      <td className="px-2 py-2 text-slate-500">{it.category || '-'}</td>
                      <td className="px-2 py-2">
                        <div className="text-slate-600">{it.price != null && it.price !== 0 ? `${it.currency || ''} ${n(it.price).toFixed(2)}` : '-'}</div>
                        <Badge tone={it.inStock ? 'green' : 'neutral'}>{it.catalogActive === false ? 'Removed from feed' : it.inStock == null ? 'Stock not provided' : it.inStock ? 'In Stock' : 'Out of Stock'}</Badge>
                      </td>
                      <td className="px-2 py-2 text-right font-medium text-slate-700">{n(it.clicks).toLocaleString()}</td>
                      <td className="px-2 py-2 text-right text-slate-600">{n(it.impressions).toLocaleString()}</td>
                      <td className="px-2 py-2 text-right text-slate-600">{n(it.recommendations).toLocaleString()}</td>
                      <td className="px-2 py-2 text-right text-slate-600">{n(it.product).toLocaleString()}</td>
                      <td className="px-2 py-2 text-right text-slate-600">{n(it.cart).toLocaleString()}</td>
                      <td className="px-2 py-2 text-right text-slate-600">{n(it.purchase).toLocaleString()}</td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={15} className="px-4 py-10 text-center text-slate-400">{search ? 'No items match your search.' : 'No items.'}</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* Modal: detalhes de tracking do item */}
      <Modal
        open={!!trackItem}
        onClose={() => setTrackItem(null)}
        title="Item Tracking"
        footer={<Button variant="outline" onClick={() => setTrackItem(null)}>Close</Button>}
      >
        {trackItem && (() => {
          const redirector = trackItem.linkId ? `https://trackingredir.com/r/${trackItem.linkId}` : null;
          const params = parseQuery(trackItem.urlwithtracking || trackItem.url);
          const Row = ({ label, value, href }: { label: string; value?: string | null; href?: string | null }) => (
            <div className="py-2 border-b border-border last:border-0">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">{label}</div>
              {value ? (
                href
                  ? <a className="text-sm text-brand-600 hover:underline break-all" href={href} target="_blank" rel="noreferrer">{value}</a>
                  : <div className="text-sm text-slate-700 break-all font-mono">{value}</div>
              ) : <div className="text-sm text-slate-300">—</div>}
            </div>
          );
          return (
            <div>
              <div className="mb-3">
                <div className="text-sm font-semibold text-ink-900">{trackItem.name || '—'}</div>
                {trackItem.itemId && <div className="text-[11px] text-slate-400 font-mono break-all">itemId: {trackItem.itemId}</div>}
              </div>

              <Row label="URL" value={trackItem.url} href={trackItem.url} />
              <Row label="URL Clean" value={trackItem.urlclean} href={trackItem.urlclean} />
              <Row label="URL with Tracking" value={trackItem.urlwithtracking} href={trackItem.urlwithtracking} />
              <Row label="LinkId" value={trackItem.linkId} />
              <Row label="Redirector" value={redirector} href={redirector} />
              <Row label="Tracking Code" value={trackItem.tracking_code} />

              <div className="mt-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                  Query string ({params.length} parameter{params.length === 1 ? '' : 's'})
                </div>
                {params.length > 0 ? (
                  <div className="rounded-lg border border-border overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-surface-muted text-left text-slate-500"><th className="px-3 py-1.5 font-medium w-44">Parameter</th><th className="px-3 py-1.5 font-medium">Value</th></tr></thead>
                      <tbody className="divide-y divide-border">
                        {params.map((p, i) => (
                          <tr key={i}>
                            <td className="px-3 py-1.5 font-mono text-slate-600 align-top break-all">{p.key}</td>
                            <td className="px-3 py-1.5 text-slate-700 break-all">{p.value || <span className="text-slate-300">(empty)</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p className="text-sm text-slate-400">No query string.</p>}
              </div>
            </div>
          );
        })()}
      </Modal>
    </Layout>
  );
}
